import { GamerSection, type SidebarNavigationItem } from '../../../shared/types/navigation'
import { PlatformTourPhase, type PlatformTourState } from '../../../store/slices/uiSlice'
import { PlatformTourOutcome } from '../../../../../pure-js/enums/PlatformTourOutcome'
import type { User } from '../../../../../pure-js/types/User'
import { isLeaderRole } from '../../organization/orgStateHelpers'

/** A page of the menu the tour stops at: its menu entry and its own translation keys. */
export interface PlatformTourStop {
  section: GamerSection
  labelKey: string
  descriptionKey: string
  highlightKeys: string[]
}

/** What the onboarding page offers about the tour right now. */
export enum PlatformTourEntry {
  OFFER = 'offer',
  RESUME = 'resume',
  NONE = 'none'
}

const STOP_KEY_PREFIX = 'gamer.platformTour.stops'
const HIGHLIGHTS_PER_STOP = 3

/** The menu pages the tour covers, keyed to their translation group. Latest Session Feedback is left out on purpose. */
const STOP_KEY_BY_SECTION: Partial<Record<GamerSection, string>> = {
  [GamerSection.DASHBOARD_GAMER]: 'dashboard',
  [GamerSection.UPCOMING_ACTIVITIES]: 'upcomingActivities',
  [GamerSection.ARIA_COACH]: 'ariaCoach',
  [GamerSection.MY_PROFILE]: 'myProfile',
  [GamerSection.ONBOARDING]: 'onboarding',
  [GamerSection.FAQ]: 'faq'
}

const toStop = (item: SidebarNavigationItem, stopKey: string): PlatformTourStop => ({
  section: item.id as GamerSection,
  labelKey: item.label,
  descriptionKey: `${STOP_KEY_PREFIX}.${stopKey}.description`,
  highlightKeys: Array.from(
    { length: HIGHLIGHTS_PER_STOP },
    (_, index) => `${STOP_KEY_PREFIX}.${stopKey}.highlight${index + 1}`
  )
})

/** The tour's stops in the order the menu shows them, for the pages this person's menu has. */
export const buildPlatformTourStops = (navigationItems: SidebarNavigationItem[]): PlatformTourStop[] =>
  navigationItems.flatMap((item) => {
    const stopKey = STOP_KEY_BY_SECTION[item.id as GamerSection]
    if (!stopKey || item.hideInSidebar) return []

    return [toStop(item, stopKey)]
  })

const PHASES_ON_SCREEN = [PlatformTourPhase.WELCOME, PlatformTourPhase.STOP, PlatformTourPhase.DONE]

/** Whether the tour covers the page right now. */
export const isPlatformTourOnScreen = (tour: PlatformTourState | undefined): boolean =>
  Boolean(tour && PHASES_ON_SCREEN.includes(tour.phase))

/** The tour walks the gamer menu; a leader's menu has no Dashboard to walk. */
export const isPlatformTourAvailable = (user: User | null | undefined): boolean => Boolean(user) && !isLeaderRole(user)

/** Offer the tour until they finished or skipped it; a tour paused in this visit offers to resume instead. */
export const resolvePlatformTourEntry = (
  tour: PlatformTourState | undefined,
  outcome: PlatformTourOutcome | undefined
): PlatformTourEntry => {
  if (tour?.phase === PlatformTourPhase.PAUSED) return PlatformTourEntry.RESUME
  if (tour || outcome) return PlatformTourEntry.NONE

  return PlatformTourEntry.OFFER
}

/** The first word of the name the dashboard greets them with. */
export const getTourGreetingName = (user: User | null | undefined): string => {
  const name = user?.displayName || user?.email?.split('@')[0] || ''
  return name.trim().split(/\s+/)[0]
}
